import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import dayjs from "dayjs";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import {
  useGetEnterpriseCopilotUsageQuery,
  useGetEnterpriseCopilotUsageMonthlyQuery,
} from "state/rtkQuery/copilot/enterpriseCopilotQueries";
import {
  useGetEnterpriseCopilotUsageByCostCenterQuery,
  useGetCopilotUsageQuery,
  useStartCopilotExportJobMutation,
  useStartCopilotBudgetExportJobMutation,
  useStartEnterpriseCopilotExportJobMutation,
  useStartEnterpriseCopilotBudgetExportJobMutation,
} from "state/rtkQuery/copilot/copilotQueries";
import StatCards from "pages/admin/components/CopilotDashboard/StatCards";
import PoolPieChart from "pages/admin/components/CopilotDashboard/PoolPieChart";
import ModelTable from "pages/admin/components/CopilotDashboard/ModelTable";
import EngineerTable from "pages/admin/components/CopilotDashboard/EngineerTable";
import UsageCharts from "pages/admin/components/CopilotDashboard/UsageCharts";
import { PRESETS, DEFAULT_PRESET_KEY, getPresetRange } from "pages/admin/components/CopilotDashboard/datePresets";
import { DEFAULT_DELOITTE_OVERAGE_RATE, useDashboardSettings } from "pages/admin/components/CopilotDashboard/useDashboardSettings";
import { useCopilotExportJob } from "pages/admin/components/CopilotDashboard/useCopilotExportJob";
import EnterpriseScopeSelector from "pages/ecrOps/components/EnterpriseScopeSelector";

const ACTION_GREEN = "#26890d";
const ACTION_BLUE = "#006eff";
const ORG_LINE_COLORS = ["#0097D7", "#7B5EA7", "#E87722", "#43B02A", "#F28C00", "#1F7AA8"];
const OVERAGE_RED = "#E2231A";

const pickerTheme = createTheme({ palette: { primary: { main: ACTION_GREEN } } });

const formatAIC = (v) => (v ?? 0).toLocaleString("en-AU", { maximumFractionDigits: 2 });
const formatUSD = (v) =>
  "$" + (v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatPct = (v) => `${(v ?? 0).toFixed(1)}%`;

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};

const bodyCellSx = {
  fontSize: "0.85rem",
  color: "#1a1a1a",
  py: 1.2,
  borderBottom: "1px solid #f0f0f0",
};

// ── Consumption by Organization — enterprise-specific, no equivalent in the
// org-scoped dashboard. Mirrors ModelTable's exact look (Paper card, header
// Box, sticky-header table with a usage progress bar per row, sticky Total
// footer) so the two "Consumption by ___" sections read as one consistent pattern.
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";
const USAGE_RED_THRESHOLD_PCT = 25;
const USAGE_ORANGE_THRESHOLD_PCT = 15;

const getUsageColor = (percentOfTotal) => {
  if (percentOfTotal >= USAGE_RED_THRESHOLD_PCT) return USAGE_RED;
  if (percentOfTotal >= USAGE_ORANGE_THRESHOLD_PCT) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const OVERAGE_COLUMN_BG = "#86bc254f";
const DISCOUNT_GREEN = "#26890d";
const LIST_OVERAGE_RATE = 0.01;

const OrgBreakdownTable = ({ orgBreakdown, deloitteOverageRate }) => {
  const effectiveDeloitteRate = deloitteOverageRate ?? DEFAULT_DELOITTE_OVERAGE_RATE;

  const totals = orgBreakdown.reduce(
    (acc, org) => ({
      grossCredits: acc.grossCredits + (org.total_gross_credits ?? 0),
      grossAmount: acc.grossAmount + (org.total_gross_credits ?? 0) * LIST_OVERAGE_RATE,
      poolSavings: acc.poolSavings + (org.total_pool_savings_amount ?? 0),
      overageCredits: acc.overageCredits + (org.total_overage_credits ?? 0),
      overageAmount: acc.overageAmount + (org.total_overage_amount ?? 0),
    }),
    { grossCredits: 0, grossAmount: 0, poolSavings: 0, overageCredits: 0, overageAmount: 0 }
  );
  const totalDeloitteCost = totals.overageCredits * effectiveDeloitteRate;

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: "12px", border: "1px solid #e8e8e8", backgroundColor: "#fff", overflow: "hidden" }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f0f0f0" }}>
        <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a" }}>
          Consumption by Organization
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
          Sorted by gross AIC consumed, descending
        </Typography>
      </Box>
      <TableContainer sx={{ minHeight: 240, maxHeight: 560, overflowY: "auto", overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 48 }}>#</TableCell>
              <TableCell sx={headerCellSx}>Organization</TableCell>
              <TableCell sx={headerCellSx} align="right">Gross AIC</TableCell>
              <TableCell sx={headerCellSx} align="right">Gross Amount</TableCell>
              <TableCell sx={headerCellSx} align="right">Pool Covered ($)</TableCell>
              <TableCell sx={{ ...headerCellSx, whiteSpace: "pre-line", lineHeight: 1.4 }} align="right">{"Overage\nBilled"}</TableCell>
              <TableCell sx={{ ...headerCellSx, whiteSpace: "pre-line", lineHeight: 1.4 }} align="right">{"Overage\nDiscount"}</TableCell>
              <TableCell sx={{ ...headerCellSx, whiteSpace: "pre-line", lineHeight: 1.4 }} align="right">{"Net Overage\nCost"}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orgBreakdown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ ...bodyCellSx, textAlign: "center", color: "#999", fontStyle: "italic", py: 4 }}>
                  No organization usage recorded for this date range.
                </TableCell>
              </TableRow>
            ) : (
              orgBreakdown.map((org, idx) => {
                const usagePercent = totals.grossCredits > 0 ? ((org.total_gross_credits ?? 0) / totals.grossCredits) * 100 : 0;
                const usageColor = getUsageColor(usagePercent);
                const grossAmount = (org.total_gross_credits ?? 0) * LIST_OVERAGE_RATE;
                const netOverageCost = (org.total_overage_credits ?? 0) * effectiveDeloitteRate;
                const overageDiscount = (org.total_overage_amount ?? 0) - netOverageCost;
                return (
                  <TableRow key={org.org_id} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell sx={{ ...bodyCellSx, color: "#999", width: 48 }}>{idx + 1}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "#1a1a1a" }}>
                          {org.org_name}
                        </Typography>
                        <Tooltip title={`${usagePercent.toFixed(1)}% of total Gross AIC`} placement="right" arrow>
                          <LinearProgress
                            variant="determinate"
                            value={usagePercent}
                            sx={{
                              mt: 0.5,
                              height: 4,
                              borderRadius: 4,
                              backgroundColor: "#f0f0f0",
                              "& .MuiLinearProgress-bar": { backgroundColor: usageColor, borderRadius: 4 },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">
                      {formatAIC(org.total_gross_credits)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">
                      {formatUSD(grossAmount)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: DISCOUNT_GREEN }} align="right">
                      {formatUSD(org.total_pool_savings_amount)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: OVERAGE_RED }} align="right">
                      {formatUSD(org.total_overage_amount)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: DISCOUNT_GREEN, backgroundColor: OVERAGE_COLUMN_BG }} align="right">
                      {formatUSD(overageDiscount)}
                    </TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: OVERAGE_RED, backgroundColor: OVERAGE_COLUMN_BG }} align="right">
                      {formatUSD(netOverageCost)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          {orgBreakdown.length > 0 && (
            <TableFooter sx={{ position: "sticky", bottom: 0, zIndex: 2 }}>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", width: 48 }} />
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: "#1a1a1a" }}>
                  Total
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700 }} align="right">
                  {formatAIC(totals.grossCredits)}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700 }} align="right">
                  {formatUSD(totals.grossAmount)}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: DISCOUNT_GREEN }} align="right">
                  {formatUSD(totals.poolSavings)}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: "#fafafa", fontWeight: 700, color: OVERAGE_RED }} align="right">
                  {formatUSD(totals.overageAmount)}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: OVERAGE_COLUMN_BG, fontWeight: 700, color: DISCOUNT_GREEN }} align="right">
                  {formatUSD(totals.overageAmount - totalDeloitteCost)}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, borderBottom: 0, borderTop: "2px solid #e8e8e8", backgroundColor: OVERAGE_COLUMN_BG, fontWeight: 700, color: OVERAGE_RED }} align="right">
                  {formatUSD(totalDeloitteCost)}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ── Monthly Trend with per-org overlay — enterprise-specific extension of the
// org-scoped dashboard's "AIC Consumed + Total Spend - Monthly Trend" chart.
const MonthlyTrendChart = ({ monthly, orgBreakdown }) => {
  const [visibleOrgIds, setVisibleOrgIds] = useState([]);

  const chartData = useMemo(() => {
    const rows = monthly.map((m) => ({
      label: m.label,
      total_gross_credits: m.total_gross_credits,
      total_overage_credits: m.total_overage_credits,
    }));
    const rowByLabel = Object.fromEntries(rows.map((r) => [r.label, r]));
    orgBreakdown.forEach((org) => {
      org.monthly.forEach((m) => {
        if (rowByLabel[m.label]) rowByLabel[m.label][`org_${org.org_id}`] = m.total_gross_credits;
      });
    });
    return rows;
  }, [monthly, orgBreakdown]);

  const toggleOrg = (orgId) => {
    setVisibleOrgIds((prev) => (prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]));
  };

  return (
    <Box sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", backgroundColor: "#fff", p: 3 }}>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
        AIC Consumed + Overage - Monthly Trend
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 2.5 }}>
        Enterprise-wide, last 12 months, independent of the date range above
        {orgBreakdown.length > 0 && " — toggle an organization below to overlay its trend"}
      </Typography>

      {monthly.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
          <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>No monthly trend data available.</Typography>
        </Box>
      ) : (
        <>
          {orgBreakdown.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
              {orgBreakdown.slice(0, 12).map((org, index) => {
                const active = visibleOrgIds.includes(org.org_id);
                const color = ORG_LINE_COLORS[index % ORG_LINE_COLORS.length];
                return (
                  <Chip
                    key={org.org_id}
                    label={org.org_name}
                    onClick={() => toggleOrg(org.org_id)}
                    size="small"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: active ? color : "#f0f0f0",
                      color: active ? "#fff" : "#666",
                      "&:hover": { backgroundColor: active ? color : "#e5e5e5" },
                    }}
                  />
                );
              })}
            </Box>
          )}

          <Box sx={{ "& svg, & svg *": { outline: "none" } }}>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#999" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: "#999" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                  width={45}
                />
                <RechartsTooltip cursor={{ stroke: "#cfcfcf", strokeWidth: 1, strokeDasharray: "4 4" }} />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} formatter={(v) => <span style={{ color: "#555" }}>{v}</span>} />
                <Line type="monotone" dataKey="total_gross_credits" name="Gross AIC" stroke={ACTION_GREEN} strokeWidth={2.5} dot={{ fill: ACTION_GREEN, r: 3, strokeWidth: 0 }} />
                <Line type="monotone" dataKey="total_overage_credits" name="Overage AIC" stroke={OVERAGE_RED} strokeWidth={2.5} dot={{ fill: OVERAGE_RED, r: 3, strokeWidth: 0 }} />
                {orgBreakdown
                  .filter((org) => visibleOrgIds.includes(org.org_id))
                  .map((org) => {
                    const index = orgBreakdown.findIndex((o) => o.org_id === org.org_id);
                    return (
                      <Line
                        key={org.org_id}
                        type="monotone"
                        dataKey={`org_${org.org_id}`}
                        name={org.org_name}
                        stroke={ORG_LINE_COLORS[index % ORG_LINE_COLORS.length]}
                        strokeWidth={1.75}
                        strokeDasharray="4 3"
                        dot={false}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

const EcrOpsCopilotEnterprise = () => {
  // Falls back to the default (first) platform until the ECR Ops platform
  // selector (EcrOpsLayout) has resolved a selection.
  const { data: platformData } = useGetPlatformsQuery();
  const selectedPlatformId = useSelector((state) => state.ecrOpsPlatform?.selectedPlatformId);
  const platformId = selectedPlatformId || platformData?._id;

  // Same global, single dashboard configuration the per-org Copilot Dashboard
  // reads from — not org-scoped, so it's safe to use unchanged here.
  const { deloitteOverageRate } = useDashboardSettings();

  const initialRange = getPresetRange(DEFAULT_PRESET_KEY);

  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [activePreset, setActivePreset] = useState(DEFAULT_PRESET_KEY);
  const [showCustom, setShowCustom] = useState(false);
  const [queryFrom, setQueryFrom] = useState(initialRange.from.format("YYYY-MM-DD"));
  const [queryTo, setQueryTo] = useState(initialRange.to.format("YYYY-MM-DD"));

  // Scope filter — "enterprise" (default, all orgs) or scoped down to one
  // Organization or one Cost Center. scopeValue is the selected org_id/cost_center_id.
  const [scopeCategory, setScopeCategory] = useState("enterprise");
  const [scopeValue, setScopeValue] = useState(null);
  const isOrgScoped = scopeCategory === "org" && Boolean(scopeValue);
  const isCostCenterScoped = scopeCategory === "costCenter" && Boolean(scopeValue);
  const isScoped = isOrgScoped || isCostCenterScoped;

  const handleScopeCategoryChange = (nextCategory) => {
    setScopeCategory(nextCategory);
    setScopeValue(null);
  };
  const handleScopeReset = () => {
    setScopeCategory("enterprise");
    setScopeValue(null);
  };

  // Enterprise-wide summary — powers the Consumption by Organization table,
  // the org value-list for the scope selector, and (when unscoped) every KPI/chart section.
  const { data, isLoading, isFetching, error: usageError } = useGetEnterpriseCopilotUsageQuery(
    { platformId, startDate: queryFrom, endDate: queryTo },
    { skip: !platformId, refetchOnMountOrArgChange: true }
  );
  const { data: monthlyData, isLoading: isMonthlyLoading } = useGetEnterpriseCopilotUsageMonthlyQuery(
    { platformId },
    { skip: !platformId, refetchOnMountOrArgChange: true }
  );

  const summary = data?.summary ?? null;
  const orgBreakdown = data?.org_breakdown ?? [];
  const monthly = monthlyData?.monthly ?? [];
  const monthlyOrgBreakdown = monthlyData?.orgBreakdown ?? [];

  // Full active-org list (independent of usage data) — resolves an org's
  // display name even when it has zero Copilot usage in the selected date
  // range and is therefore absent from orgBreakdown (which is usage-derived
  // and only lists orgs with activity in range).
  const { data: activeOrgs } = useGetActiveOrganizationsQuery();
  const orgNameById = useMemo(
    () => Object.fromEntries((activeOrgs ?? []).map((org) => [org._id, org.name])),
    [activeOrgs]
  );

  // Cost-center value list — enterprise-wide, so it's independent of any org
  // scoping. Also doubles as the source of the selected cost center's own
  // scoped summary once picked (its `summary` field is that cost center's
  // true total across every org).
  const { data: costCenterListData, isFetching: isCostCenterListFetching } = useGetEnterpriseCopilotUsageByCostCenterQuery(
    { platformId, startDate: queryFrom, endDate: queryTo },
    { skip: !platformId, refetchOnMountOrArgChange: true }
  );
  const costCenterGroups = costCenterListData?.groups ?? [];
  const selectedCostCenterGroup = isCostCenterScoped
    ? costCenterGroups.find((g) => g.cost_center_id === scopeValue) ?? null
    : null;

  // Selected org's own scoped usage — same endpoint the per-org admin
  // dashboard uses, so switching to "Organization" scope shows exactly what
  // that org's own Copilot Dashboard page would show.
  const { data: orgUsageData, isLoading: isOrgUsageLoading, isFetching: isOrgUsageFetching } = useGetCopilotUsageQuery(
    { orgId: scopeValue, startDate: queryFrom, endDate: queryTo },
    { skip: !isOrgScoped, refetchOnMountOrArgChange: true }
  );

  const scopedSummary = isOrgScoped ? orgUsageData?.summary ?? null : isCostCenterScoped ? selectedCostCenterGroup?.summary ?? null : null;
  const displaySummary = isScoped ? scopedSummary : summary;
  const modelData = displaySummary?.usage_stats ?? [];

  // Daily per-model trend — only the org-scoped usage endpoint returns
  // day-by-day records; the enterprise-wide and cost-center summaries only
  // have range-aggregate totals, so this chart is available for Organization
  // scope only.
  const dailyRecords = isOrgScoped ? orgUsageData?.records ?? [] : [];

  const isLoadingAny =
    isLoading ||
    isFetching ||
    isMonthlyLoading ||
    isCostCenterListFetching ||
    (isOrgScoped && (isOrgUsageLoading || isOrgUsageFetching));
  const hasData = isScoped ? Boolean(displaySummary) : Boolean(summary) && data?.total !== 0;

  const statsSummaryData = displaySummary
    ? {
        totalAICConsumed: displaySummary.total_gross_credits ?? 0,
        totalSpend: (displaySummary.total_gross_credits ?? 0) * 0.01,
        totalPoolSavings: displaySummary.total_pool_savings_amount ?? 0,
        totalOverageBilled: displaySummary.total_overage_amount ?? 0,
        deloitteCost: (displaySummary.total_overage_credits ?? 0) * deloitteOverageRate,
      }
    : null;

  const poolChartData = displaySummary
    ? {
        total_pool_covered_credits: displaySummary.total_pool_covered_credits ?? 0,
        total_overage_credits: displaySummary.total_overage_credits ?? 0,
        pool_utilization_pct: displaySummary.pool_utilization_pct ?? 0,
        total_pool_savings_amount: displaySummary.total_pool_savings_amount ?? 0,
        total_overage_amount: displaySummary.total_overage_amount ?? 0,
      }
    : null;

  // Scope-aware label for export tooltips/filenames. orgBreakdown is checked
  // first since it's already loaded and covers the common case, but it only
  // lists orgs with usage in the current date range — orgNameById (the full
  // active-org list, independent of usage data) is the fallback for an org
  // scoped-to that happens to have zero usage in range (e.g. after narrowing
  // the date filter to a range with no activity).
  const scopeName = isOrgScoped
    ? orgBreakdown.find((o) => o.org_id === scopeValue)?.org_name ?? orgNameById[scopeValue] ?? scopeValue
    : isCostCenterScoped
      ? selectedCostCenterGroup?.cost_center_name ?? scopeValue
      : "enterprise";

  const handleApplyDateFilter = () => {
    setQueryFrom(fromDate.format("YYYY-MM-DD"));
    setQueryTo(toDate.format("YYYY-MM-DD"));
  };

  const handlePresetClick = (preset) => {
    setActivePreset(preset.key);
    if (preset.key === "custom") {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    const { from, to } = getPresetRange(preset.key);
    setFromDate(from);
    setToDate(to);
    setQueryFrom(from.format("YYYY-MM-DD"));
    setQueryTo(to.format("YYYY-MM-DD"));
  };

  // Export handlers — async job flow shared with CopilotDashboard.js via
  // useCopilotExportJob (start job -> poll status -> fetch signed download
  // URL); "enterprise" namespaces this dashboard's persisted jobs so they
  // never collide with the org-admin dashboard's. When scoped to an
  // org/cost center, the job is started via the same scoped job-start
  // endpoints the per-org dashboard uses (exactly-one-of org_id/cost_center_id);
  // otherwise the enterprise-wide job-start endpoints. Status polling and
  // download are identical regardless of how the job was started.
  const [startScopedExportJob] = useStartCopilotExportJobMutation();
  const [startScopedBudgetExportJob] = useStartCopilotBudgetExportJobMutation();
  const [startEnterpriseExportJob] = useStartEnterpriseCopilotExportJobMutation();
  const [startEnterpriseBudgetExportJob] = useStartEnterpriseCopilotBudgetExportJobMutation();
  const { isExporting, isExportingBudget, makeHandlers } = useCopilotExportJob("enterprise");

  const { handleExport, handleBudgetExport } = makeHandlers({
    platformId,
    exportScopeName: scopeName,
    queryFrom,
    queryTo,
    startUsageJob: () =>
      (isScoped
        ? startScopedExportJob({ orgId: isOrgScoped ? scopeValue : undefined, costCenterId: isCostCenterScoped ? scopeValue : undefined, platformId, fromDate: queryFrom, toDate: queryTo })
        : startEnterpriseExportJob({ platformId, fromDate: queryFrom, toDate: queryTo })
      ).unwrap(),
    startBudgetJob: () =>
      (isScoped
        ? startScopedBudgetExportJob({ orgId: isOrgScoped ? scopeValue : undefined, costCenterId: isCostCenterScoped ? scopeValue : undefined, platformId, fromDate: queryFrom, toDate: queryTo })
        : startEnterpriseBudgetExportJob({ platformId, fromDate: queryFrom, toDate: queryTo })
      ).unwrap(),
  });

  return (
    <Box
      sx={{
        flex: "1",
        backgroundColor: "#F3F3F3",
        borderRadius: "12px",
        padding: { xs: "16px", md: "24px" },
      }}
    >
      {/* Page Header — same layout as CopilotDashboard.js */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: { xs: "flex-start", lg: "center" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <BusinessOutlined sx={{ fontSize: 28, color: "#1a1a1a" }} />
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: "#43B02A",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                Enterprise
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>
              Copilot Dashboard
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#888", mt: 0.3 }}>
              {isScoped
                ? `AIC usage & spend analytics — scoped to ${scopeName}`
                : "AIC usage & spend analytics — aggregated across every organization"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                mt: 1,
                px: 1.2,
                py: 0.35,
                backgroundColor: "rgba(38,137,13,0.07)",
                border: "1px solid rgba(38,137,13,0.22)",
                borderRadius: "20px",
                width: "fit-content",
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: "12px", color: "#26890d" }} />
              <Typography sx={{ fontSize: "12px", color: "#26890d", fontWeight: 700, letterSpacing: "0.01em" }}>
                {queryFrom} &rarr; {queryTo}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right side: Export buttons + date controls — identical styling to CopilotDashboard.js */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", lg: "flex-end" },
            gap: 1,
            width: { xs: "100%", lg: "auto" },
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Tooltip title={!hasData ? "No data to export for this range" : `Exports usage data for ${scopeName}`} placement="top" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isExporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={isExporting || !platformId || !hasData}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    borderColor: "#ccc",
                    color: "#555",
                    px: 1.5,
                    py: 0.3,
                    minWidth: 0,
                    "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN },
                    "&.Mui-disabled": {
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      borderColor: "#e0e0e0",
                      color: "#bbb",
                    },
                  }}
                >
                  {isExporting ? "Exporting..." : "Export Usage Data"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={!hasData ? "No data to export for this range" : `Exports budget data for ${scopeName}`} placement="top" arrow>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isExportingBudget ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
                  onClick={handleBudgetExport}
                  disabled={isExportingBudget || !platformId || !hasData}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    borderColor: "#ccc",
                    color: "#555",
                    px: 1.5,
                    py: 0.3,
                    minWidth: 0,
                    "&:hover": { borderColor: ACTION_GREEN, color: ACTION_GREEN },
                    "&.Mui-disabled": {
                      borderRadius: "16px",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      borderColor: "#e0e0e0",
                      color: "#bbb",
                    },
                  }}
                >
                  {isExportingBudget ? "Exporting..." : "Export Budget"}
                </Button>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                size="small"
                onClick={() => handlePresetClick(p)}
                sx={{
                  borderRadius: "16px",
                  textTransform: "none",
                  fontSize: "0.8rem",
                  fontWeight: activePreset === p.key ? 600 : 400,
                  backgroundColor: activePreset === p.key ? ACTION_GREEN : "transparent",
                  color: activePreset === p.key ? "#fff" : "#555",
                  border: `1px solid ${activePreset === p.key ? ACTION_GREEN : "#ccc"}`,
                  px: 1.5,
                  py: 0.3,
                  minWidth: 0,
                  "&:hover": {
                    backgroundColor: activePreset === p.key ? ACTION_GREEN : "#f0f0f0",
                    border: `1px solid ${activePreset === p.key ? ACTION_GREEN : "#bbb"}`,
                  },
                }}
              >
                {p.label}
              </Button>
            ))}
          </Box>

          {showCustom && (
            <ThemeProvider theme={pickerTheme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <DatePicker
                    label="From"
                    value={fromDate}
                    onChange={(val) => setFromDate(val)}
                    maxDate={toDate}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ backgroundColor: "#fff", borderRadius: 1, width: 150 }} />
                    )}
                  />
                  <DatePicker
                    label="To"
                    value={toDate}
                    onChange={(val) => setToDate(val)}
                    minDate={fromDate}
                    maxDate={dayjs()}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ backgroundColor: "#fff", borderRadius: 1, width: 150 }} />
                    )}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SearchIcon />}
                    onClick={handleApplyDateFilter}
                    disabled={isLoadingAny}
                    sx={{
                      backgroundColor: ACTION_GREEN,
                      color: "#fff",
                      fontWeight: 400,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      height: 35,
                      minHeight: 35,
                      padding: "0 10px",
                      lineHeight: 1,
                      "& .MuiButton-startIcon": { marginRight: "4px" },
                      "&:hover": { backgroundColor: ACTION_GREEN },
                      "&:disabled": { backgroundColor: "#ccc", color: "#999" },
                    }}
                  >
                    {isLoadingAny ? "Fetching..." : "Apply"}
                  </Button>
                </Box>
              </LocalizationProvider>
            </ThemeProvider>
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <EnterpriseScopeSelector
        category={scopeCategory}
        value={scopeValue}
        orgOptions={orgBreakdown}
        costCenterOptions={costCenterGroups}
        orgNameById={orgNameById}
        loading={isCostCenterListFetching}
        onCategoryChange={handleScopeCategoryChange}
        onValueChange={setScopeValue}
        onReset={handleScopeReset}
      />

      {/* Loading Progress Bar */}
      {isLoadingAny && <LinearProgress sx={{ mb: 2, backgroundColor: "#f0f0f0", "& .MuiLinearProgress-bar": { backgroundColor: ACTION_GREEN } }} />}

      {isLoadingAny && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
            backgroundColor: "#fafafa",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress sx={{ color: ACTION_BLUE }} />
            <Typography sx={{ fontSize: "0.9rem", color: "#888" }}>Fetching data...</Typography>
          </Box>
        </Box>
      )}

      {!isLoadingAny && usageError && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#E53935", fontWeight: 600 }}>
            {usageError?.data?.detail ?? "Failed to load usage data. Please try again."}
          </Typography>
        </Box>
      )}

      {!isLoadingAny && !usageError && !hasData && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
            backgroundColor: "#fafafa",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", color: "#999" }}>
            {isScoped ? `No usage data available for ${scopeName} in this date range.` : "No usage data available for this date range."}
          </Typography>
        </Box>
      )}

      {!isLoadingAny && !usageError && hasData && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Section 1 - Stat Cards — identical component to the org-scoped dashboard */}
          {statsSummaryData && (
            <StatCards
              data={statsSummaryData}
              deloitteOverageRate={deloitteOverageRate}
              orgId={isOrgScoped ? scopeValue : undefined}
              costCenterId={isCostCenterScoped ? scopeValue : undefined}
              platformId={platformId}
              isEnterprise={!isScoped}
            />
          )}

          {/* Section 2 - Pie Chart + Model Table side by side — identical components */}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "stretch" }}>
            <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 280px" } }}>
              {poolChartData && <PoolPieChart data={poolChartData} />}
            </Box>
            <Box sx={{ flex: "1", minWidth: 0, border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
              <ModelTable
                models={modelData}
                totalOverageCredits={displaySummary?.total_overage_credits ?? 0}
                totalOverageAmount={displaySummary?.total_overage_amount ?? 0}
                deloitteOverageRate={deloitteOverageRate}
                orgId={isOrgScoped ? scopeValue : undefined}
                costCenterId={isCostCenterScoped ? scopeValue : undefined}
                platformId={platformId}
                fromDate={queryFrom}
                toDate={queryTo}
                isEnterprise={!isScoped}
              />
            </Box>
          </Box>

          {/* Section 3 - Engineer Table — identical component, with an Org column shown only when enterprise-wide */}
          <Box sx={{ border: "1px solid #e8e8e8", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
            <EngineerTable
              orgId={isOrgScoped ? scopeValue : undefined}
              costCenterId={isCostCenterScoped ? scopeValue : undefined}
              platformId={platformId}
              fromDate={queryFrom}
              toDate={queryTo}
              isEnterprise={!isScoped}
            />
          </Box>

          {/* Section 3b - Usage Grouped by Model — only available once a specific
              Organization is selected, since only that scope's usage endpoint
              returns day-by-day records; enterprise-wide and cost-center summaries
              are range-aggregate only. */}
          {isOrgScoped && dailyRecords.length > 0 && (
            <UsageCharts
              data={[]}
              dailyRecords={dailyRecords}
              fromDate={queryFrom}
              toDate={queryTo}
              showMonthlyTrend={false}
              showPoolCoverageTrend={false}
            />
          )}

          {/* Section 4 - Consumption by Organization — enterprise-only, hidden once scoped to a single org/cost center */}
          {!isScoped && <OrgBreakdownTable orgBreakdown={orgBreakdown} deloitteOverageRate={deloitteOverageRate} />}

          {/* Section 5 - Monthly Trend with per-org overlay — enterprise-only, same reasoning */}
          {!isScoped && <MonthlyTrendChart monthly={monthly} orgBreakdown={monthlyOrgBreakdown} />}
        </Box>
      )}
    </Box>
  );
};

export default EcrOpsCopilotEnterprise;
